import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  TrophyIcon,
  GraduationCapIcon,
  LinkIcon,
  CopyIcon,
  UsersIcon,
  CalendarIcon
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import learningLevelsData from '../../data/learningLevels.json';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const CompetitionParticipants = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const { competitionId } = useParams();
  const { user } = useAuth();

  // State
  const [competition, setCompetition] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalParticipants, setTotalParticipants] = useState(0);
  const [limit] = useState(50);
  const [copySuccess, setCopySuccess] = useState(false);

  // Helper function to translate level name
  const translateLevelName = (levelName) => {
    if (!levelName) return '';
    const translations = learningLevelsData[levelName] || {};
    return translations[currentLanguage] || levelName;
  };

  // Fetch competition details
  const fetchCompetitionDetails = async () => {
    try {
      const response = await api.get(`/competitions/${competitionId}`);
      
      if (response.data.success) {
        setCompetition(response.data.data);
      } else {
        toast.error(t('competitions.notFound'), { duration: 3000, position: 'top-center' });
      }
    } catch (error) {
      console.error('Error fetching competition details:', error);
      toast.error(t('competitions.fetchError'), { duration: 3000, position: 'top-center' });
    }
  };

  // Fetch participants
  const fetchParticipants = async (page = 1) => {
    setLoading(true);
    try {
      let url = `/competitions/${competitionId}/participants?page=${page}&limit=${limit}`;
      
      const response = await api.get(url);
      
      if (response.data.success) {
        const participantsData = response.data.data?.participants || response.data.data || [];
        setParticipants(participantsData);
        setTotalPages(response.data.data?.pagination?.pages || 1);
        setTotalParticipants(response.data.data?.pagination?.total || participantsData.length);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error('Error fetching participants:', error);
      toast.error(t('competitions.studentsFetchError'), { duration: 3000, position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchCompetitionDetails();
    fetchParticipants();
  }, [competitionId]);

  // Handle page change
  const handlePageChange = (page) => {
    fetchParticipants(page);
  };

  // Copy public link
  const copyPublicLink = () => {
    const publicUrl = `${window.location.origin}/public/competitions/${competitionId}/participants`;
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopySuccess(true);
      toast.success(t('common.linkCopied'), { duration: 2000, position: 'top-center' });
      setTimeout(() => setCopySuccess(false), 2000);
    }).catch(() => {
      toast.error(t('common.copyFailed'), { duration: 2000, position: 'top-center' });
    });
  };

  if (!competition) {
    return <LoadingSpinner />;
  }

  return (
    <div className={`p-6 ${isRTL ? 'font-arabic' : ''}`}>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-4 transition-colors"
        >
          <ArrowLeftIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
          {t('common.back')}
        </button>

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <TrophyIcon className={`h-8 w-8 text-primary-600 ${isRTL ? 'ml-3' : 'mr-3'}`} />
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('competitions.participantsList')}
              </h1>
              <p className="text-gray-600 mt-1">
                {competition?.name || t('common.loading')}
              </p>
            </div>
          </div>
          
          <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button
              onClick={() => {
                const userRole = user?.role?.toLowerCase().replace('_', '-') || 'super-admin';
                navigate(`/dashboard/competitions/${competitionId}/leaderboard`);
              }}
              className="flex items-center px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
            >
              <TrophyIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('competitions.viewLeaderboard')}
            </button>
            
            <button
              onClick={copyPublicLink}
              className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors ${copySuccess ? 'bg-green-600 hover:bg-green-700' : ''}`}
            >
              {copySuccess ? (
                <>
                  <CheckCircleIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('common.copied')}
                </>
              ) : (
                <>
                  <CopyIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('competitions.copyPublicLink')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Competition Info Cards */}
      {competition && (
        <div className="mb-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Description Card */}
          <div className="bg-white border-l-4 border-primary-500 shadow-md rounded-lg p-5">
            <div className="flex items-center mb-4">
              <div className="bg-primary-100 p-2.5 rounded-lg">
                <TrophyIcon className="h-5 w-5 text-primary-600" />
              </div>
              <h3 className={`text-base font-bold text-gray-800 ${isRTL ? 'mr-3' : 'ml-3'}`}>
                {t('competitions.description')}
              </h3>
            </div>
            <div className={`${isRTL ? 'text-right' : 'text-left'} space-y-2`}>
              <div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('competitions.competitionTitle')}</span>
                <p className="text-sm font-bold text-gray-900 mt-1">{competition.name}</p>
              </div>
              <div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('competitions.description')}</span>
                <p className="text-sm text-gray-700 leading-relaxed mt-1">
                  {competition.description || '-'}
                </p>
              </div>
            </div>
          </div>
          
          {/* Statistics Card */}
          <div className="bg-white border-l-4 border-green-500 shadow-md rounded-lg p-5">
            <div className="flex items-center mb-4">
              <div className="bg-green-100 p-2.5 rounded-lg">
                <UsersIcon className="h-5 w-5 text-green-600" />
              </div>
              <h3 className={`text-base font-bold text-gray-800 ${isRTL ? 'mr-3' : 'ml-3'}`}>
                {t('competitions.statistics')}
              </h3>
            </div>
            <div className={`space-y-3 ${isRTL ? 'text-right' : 'text-left'}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t('competitions.totalParticipants')}</span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-primary-100 text-primary-700">
                  {competition.stats?.totalParticipants || totalParticipants}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t('competitions.rounds')}</span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-blue-100 text-blue-700">
                  {competition.totalRounds}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t('competitions.maxParticipants')}</span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-green-100 text-green-700">
                  {competition.settings?.maxParticipants || '-'}
                </span>
              </div>
            </div>
          </div>
          
          {/* Schedule Card */}
          <div className="bg-white border-l-4 border-blue-500 shadow-md rounded-lg p-5">
            <div className="flex items-center mb-4">
              <div className="bg-blue-100 p-2.5 rounded-lg">
                <CalendarIcon className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className={`text-base font-bold text-gray-800 ${isRTL ? 'mr-3' : 'ml-3'}`}>
                {t('competitions.schedule')}
              </h3>
            </div>
            <div className={`space-y-3 ${isRTL ? 'text-right' : 'text-left'}`}>
              <div className="bg-green-50 border-l-2 border-green-400 px-3 py-2.5 rounded">
                <div className="text-xs text-green-700 font-semibold mb-1.5 uppercase tracking-wide">
                  {t('competitions.competitionStart')}
                </div>
                <div className="text-sm font-bold text-green-900">
                  📅 {new Date(competition.schedule?.competitionStart).toLocaleDateString(currentLanguage === 'ar' ? 'ar-SA' : currentLanguage === 'fr' ? 'fr-FR' : 'en-US', { 
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                  })} 🕐 {new Date(competition.schedule?.competitionStart).toLocaleTimeString(currentLanguage === 'ar' ? 'ar-SA' : currentLanguage === 'fr' ? 'fr-FR' : 'en-US', { 
                    hour12: false,
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
              <div className="bg-red-50 border-l-2 border-red-400 px-3 py-2.5 rounded">
                <div className="text-xs text-red-700 font-semibold mb-1.5 uppercase tracking-wide">
                  {t('competitions.competitionEnd')}
                </div>
                <div className="text-sm font-bold text-red-900">
                  📅 {new Date(competition.schedule?.competitionEnd).toLocaleDateString(currentLanguage === 'ar' ? 'ar-SA' : currentLanguage === 'fr' ? 'fr-FR' : 'en-US', { 
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                  })} 🕐 {new Date(competition.schedule?.competitionEnd).toLocaleTimeString(currentLanguage === 'ar' ? 'ar-SA' : currentLanguage === 'fr' ? 'fr-FR' : 'en-US', { 
                    hour12: false,
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Participants Table */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {participants.length === 0 ? (
            <div className="text-center py-12">
              <GraduationCapIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">{t('competitions.noStudents')}</h3>
              <p className="mt-1 text-sm text-gray-500">{t('competitions.noStudentsDesc')}</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('users.students.name')}
                      </th>
                      <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                        User ID
                      </th>
                      <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('users.students.studentNumber')}
                      </th>
                      <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('users.students.level')}
                      </th>
                      <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('competitions.currentRound')}
                      </th>
                      <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('competitions.completedRounds')}
                      </th>
                      <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('competitions.totalScore')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {participants.map((participant) => (
                      <tr key={participant.id} className="hover:bg-gray-50">
                        <td className={`px-6 py-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                <GraduationCapIcon className="h-5 w-5 text-primary-600" />
                              </div>
                            </div>
                            <div className={`${isRTL ? 'mr-4' : 'ml-4'}`}>
                              <div className="text-sm font-medium text-gray-900">
                                {participant.fullName || `${participant.firstName} ${participant.lastName}`}
                              </div>
                              <div className="text-sm text-gray-500">
                                @{participant.username}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            #{participant.studentUserId}
                          </span>
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {participant.studentNumber}
                          </span>
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                          <div className="text-sm text-gray-900">
                            {translateLevelName(participant.levelName)}
                          </div>
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                          <div className="text-sm text-gray-900 text-center">
                            {participant.currentRound || 1}
                          </div>
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                          <div className="text-sm text-gray-900 text-center">
                            {participant.completedRounds || 0}
                          </div>
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-yellow-100 text-yellow-800">
                            {parseFloat(participant.totalScore || 0).toFixed(2)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Enhanced Pagination */}
              {totalPages > 1 && (
                <div className={`bg-gray-50 px-6 py-4 border-t border-gray-200`}>
                  <div className={`flex flex-col sm:flex-row items-center ${isRTL ? 'sm:flex-row-reverse' : ''} justify-between gap-4`}>
                    {/* Info */}
                    <div className="text-sm text-gray-700">
                      {t('competitions.showing')} <span className="font-bold text-primary-600">{((currentPage - 1) * limit) + 1}</span> {t('competitions.to')}{' '}
                      <span className="font-bold text-primary-600">{Math.min(currentPage * limit, totalParticipants)}</span> {t('competitions.of')}{' '}
                      <span className="font-bold text-primary-600">{totalParticipants}</span> {t('competitions.students')}
                    </div>
                    
                    {/* Page Numbers */}
                    <div className={`flex ${isRTL ? 'flex-row-reverse space-x-reverse' : ''} items-center gap-2`}>
                      {/* Previous */}
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >
                        {t('common.previous')}
                      </button>
                      
                      {/* Page Numbers */}
                      <div className={`flex ${isRTL ? 'flex-row-reverse space-x-reverse' : ''} items-center gap-1`}>
                        {/* First Page */}
                        {currentPage > 3 && (
                          <>
                            <button
                              onClick={() => handlePageChange(1)}
                              className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              1
                            </button>
                            {currentPage > 4 && <span className="px-2 text-gray-400">...</span>}
                          </>
                        )}
                        
                        {/* Pages around current */}
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          if (pageNum < 1 || pageNum > totalPages) return null;
                          if (currentPage > 3 && pageNum === 1) return null;
                          if (currentPage < totalPages - 2 && pageNum === totalPages) return null;
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                                currentPage === pageNum
                                  ? 'bg-primary-600 text-white border-primary-600'
                                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        
                        {/* Last Page */}
                        {currentPage < totalPages - 2 && (
                          <>
                            {currentPage < totalPages - 3 && <span className="px-2 text-gray-400">...</span>}
                            <button
                              onClick={() => handlePageChange(totalPages)}
                              className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              {totalPages}
                            </button>
                          </>
                        )}
                      </div>
                      
                      {/* Next */}
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >
                        {t('common.next')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CompetitionParticipants;

